import React, { useState, useRef, useEffect } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const TimeClockGame = ({ gameData, onComplete }) => {
    const [currentTimeIndex, setCurrentTimeIndex] = useState(0);
    const [hourAngle, setHourAngle] = useState(0);
    const [minuteAngle, setMinuteAngle] = useState(0);
    const [feedback, setFeedback] = useState(null);
    const [score, setScore] = useState(0);
    const [isDragging, setIsDragging] = useState(null);
    const clockRef = useRef(null);

    const currentTime = gameData?.times?.[currentTimeIndex];

    const calculateAngle = (clientX, clientY) => {
        if (!clockRef.current) return 0;
        const clock = clockRef.current.getBoundingClientRect();
        const centerX = clock.left + clock.width / 2;
        const centerY = clock.top + clock.height / 2;
        const dx = clientX - centerX;
        const dy = clientY - centerY;
        let angle = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
        if (angle < 0) angle += 360;
        return angle;
    };

    const handleStartDragging = (hand, e) => {
        if (feedback) return;
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        setIsDragging(hand);
        triggerHaptic(40);

        const angle = calculateAngle(clientX, clientY);
        if (hand === 'hour') setHourAngle(Math.round(angle / 30) * 30);
        if (hand === 'minute') setMinuteAngle(Math.round(angle / 6) * 6);
    };

    const handleMoving = (e) => {
        if (!isDragging || feedback) return;

        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        const angle = calculateAngle(clientX, clientY);

        if (isDragging === 'hour') {
            const snappedAngle = Math.round(angle / 30) * 30;
            if (snappedAngle !== hourAngle) {
                setHourAngle(snappedAngle);
                triggerHaptic(15);
            }
        } else if (isDragging === 'minute') {
            const snappedAngle = Math.round(angle / 6) * 6;
            if (snappedAngle !== minuteAngle) {
                setMinuteAngle(snappedAngle);
                triggerHaptic(8);
            }
        }
    };

    const handleStopDragging = () => {
        setIsDragging(null);
    };

    const checkAnswer = () => {
        if (feedback) return;
        const hourFromAngle = Math.round((hourAngle / 30) % 12) || 12;
        const minuteFromAngle = Math.round((minuteAngle / 6) % 60);

        const targetHour12 = (currentTime.hours % 12) || 12;
        const targetMinute = currentTime.minutes;

        const isCorrect = hourFromAngle === targetHour12 && Math.abs(minuteFromAngle - targetMinute) <= 2;

        if (isCorrect) {
            setFeedback('correct');
            const pointsToAdd = gameData.points_per_time || 20;
            const newScore = score + pointsToAdd;
            setScore(newScore);
            triggerHaptic([100, 50, 100]);

            setTimeout(() => {
                if (currentTimeIndex + 1 >= gameData.times.length) {
                    triggerHaptic([200, 100, 200, 100, 300]);
                    onComplete?.(newScore);
                } else {
                    setCurrentTimeIndex(prev => prev + 1);
                    setHourAngle(0);
                    setMinuteAngle(0);
                    setFeedback(null);
                    triggerHaptic(30);
                }
            }, 4000);
        } else {
            setFeedback('wrong');
            triggerHaptic([50, 50, 50]);
            setTimeout(() => setFeedback(null), 2000);
        }
    };

    const resetClock = () => {
        if (feedback) return;
        setHourAngle(0);
        setMinuteAngle(0);
        triggerHaptic(60);
    };

    if (!gameData || !currentTime) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-violet-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">⏰</div>
                <h3 className="text-base md:text-3xl font-black text-violet-900 tracking-tighter uppercase">Nustatomas laikas...</h3>
            </div>
        );
    }

    const progress = (currentTimeIndex / gameData.times.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-violet-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-violet-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        ⏰
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Laiko <span className="text-violet-600">Meistras</span>
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Chronos</p>
                        <p className="text-sm md:text-2xl font-black text-violet-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Etapas</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentTimeIndex + 1}/{gameData.times.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div
                    className="h-full bg-gradient-to-r from-violet-400 via-indigo-500 to-purple-600 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(139,92,246,0.4)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-6 md:p-12 text-center border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center h-full justify-center shadow-2xl">
                <div className="absolute top-0 right-0 p-4 md:p-12 text-6xl md:text-[200px] opacity-[0.03] select-none pointer-events-none rotate-12">📅</div>
                <div className="text-center space-y-4 md:space-y-10 w-full relative z-10">
                    <h4 className="text-[8px] md:text-[10px] font-black text-violet-500 uppercase tracking-[0.6em]">Dienos Planas</h4>
                    <div className="bg-white rounded-2xl md:rounded-[3rem] p-4 md:p-8 shadow-inner border-2 border-violet-50">
                        <h3 className="text-base md:text-3xl font-black text-slate-900 tracking-tighter leading-tight italic font-serif">
                            "{currentTime.scenario}"
                        </h3>
                    </div>

                    <div className="space-y-4">
                        <h4 className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-[0.4em]">Tikslus Laikas</h4>
                        <div className="bg-slate-950 text-cyan-400 px-8 py-4 md:px-12 md:py-8 rounded-2xl md:rounded-[3.5rem] shadow-[0_20px_60px_rgba(34,211,238,0.3)] border-4 md:border-[8px] border-slate-900 inline-block relative group overflow-hidden">
                            <div className="absolute inset-0 bg-cyan-500/5 animate-pulse" />
                            <p className="text-4xl md:text-8xl font-mono font-black tracking-widest drop-shadow-[0_0_30px_rgba(34,211,238,0.4)] relative z-10">
                                {currentTime.digital}
                            </p>
                        </div>
                    </div>

                    <p className="text-xs md:text-xl font-bold text-slate-500 italic font-serif">"{currentTime.instruction}"</p>
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="flex flex-col items-center justify-center w-full max-w-4xl mx-auto py-4 md:py-8">
            <div className="premium-glass rounded-[2rem] md:rounded-[5rem] p-4 md:p-12 shadow-2xl border-white/40 bg-slate-100/30 backdrop-blur-md relative overflow-hidden flex flex-col items-center w-full touch-none">
                <div
                    ref={clockRef}
                    onMouseMove={handleMoving}
                    onMouseUp={handleStopDragging}
                    onMouseLeave={handleStopDragging}
                    onTouchMove={handleMoving}
                    onTouchEnd={handleStopDragging}
                    className="relative w-[280px] h-[280px] sm:w-[450px] sm:h-[450px] lg:w-[500px] lg:h-[500px] rounded-full bg-white shadow-[0_40px_80px_rgba(0,0,0,0.2),inset_0_0_0_10px_#f8fafc,inset_0_0_0_20px_#e2e8f0] border-[15px] md:border-[25px] border-slate-900 relative z-10 transition-transform duration-700 hover:scale-[1.01]"
                >
                    {/* Hour Markings */}
                    {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((num) => {
                        const angle = (num * 30 - 90) * (Math.PI / 180);
                        const x = 50 + 40 * Math.cos(angle);
                        const y = 50 + 40 * Math.sin(angle);
                        return (
                            <div
                                key={num}
                                className="absolute text-xl md:text-5xl font-black text-slate-900 select-none tracking-tighter italic font-serif"
                                style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)' }}
                            >
                                {num}
                            </div>
                        );
                    })}

                    {/* Celestial Hands */}
                    <div className="absolute inset-0 pointer-events-none">
                        {/* Minute Hand */}
                        <div
                            onMouseDown={(e) => handleStartDragging('minute', e)}
                            onTouchStart={(e) => handleStartDragging('minute', e)}
                            className={`
                                absolute bg-gradient-to-t from-violet-600 to-indigo-500 rounded-full cursor-grab active:cursor-grabbing shadow-lg pointer-events-auto
                                ${isDragging === 'minute' ? 'ring-[20px] ring-violet-400/20 scale-105' : 'hover:scale-102'}
                                transition-all duration-200
                            `}
                            style={{
                                width: '8px',
                                height: '44%',
                                left: 'calc(50% - 4px)',
                                top: '6%',
                                transformOrigin: '4px 100%',
                                transform: `rotate(${minuteAngle}deg)`,
                                zIndex: 10
                            }}
                        >
                            <div className="absolute top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white rounded-full opacity-50" />
                        </div>

                        {/* Hour Hand */}
                        <div
                            onMouseDown={(e) => handleStartDragging('hour', e)}
                            onTouchStart={(e) => handleStartDragging('hour', e)}
                            className={`
                                absolute bg-slate-900 rounded-full cursor-grab active:cursor-grabbing shadow-xl pointer-events-auto
                                ${isDragging === 'hour' ? 'ring-[20px] ring-slate-400/20 scale-105' : 'hover:scale-102'}
                                transition-all duration-200 border-2 border-slate-800
                            `}
                            style={{
                                width: '14px',
                                height: '32%',
                                left: 'calc(50% - 7px)',
                                top: '18%',
                                transformOrigin: '7px 100%',
                                transform: `rotate(${hourAngle}deg)`,
                                zIndex: 11
                            }}
                        />
                    </div>

                    {/* Axle */}
                    <div className="absolute w-8 h-8 md:w-12 md:h-12 bg-rose-500 rounded-full shadow-2xl border-4 md:border-8 border-white top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                        <div className="absolute inset-0 bg-white/20 animate-ping rounded-full" />
                    </div>
                </div>

                <div className="flex gap-2 md:gap-4 w-full max-w-2xl mt-8">
                    <button
                        onClick={resetClock}
                        disabled={!!feedback}
                        className="flex-1 py-3 md:py-6 bg-white/80 text-slate-500 rounded-xl md:rounded-2xl font-black text-xs md:text-xl border-b-4 md:border-b-8 border-slate-200 hover:bg-white active:translate-y-1 active:border-b-0 transition-all disabled:opacity-20 uppercase tracking-widest shadow-lg"
                    >
                        VALYTI 🔄
                    </button>
                    <button
                        onClick={checkAnswer}
                        disabled={!!feedback}
                        className={`
                            flex-[2] py-3 md:py-6 bg-gradient-to-r from-violet-600 via-indigo-600 to-purple-800 text-white rounded-xl md:rounded-2xl font-black text-xs md:text-2xl shadow-xl transition-all border-b-4 md:border-b-8 border-violet-900 uppercase tracking-[0.2em] flex items-center justify-center gap-2
                            ${!feedback ? 'hover:scale-105 active:translate-y-1 active:border-b-0' : 'opacity-20'}
                        `}
                    >
                        <span>TIKRINTI</span>
                        <span className="animate-bounce">✓</span>
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-6 md:p-12 rounded-[2.5rem] md:rounded-[4rem] shadow-2xl border-[10px] md:border-[16px] animate-pop-in text-center bg-white
                        ${feedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-white rounded-full flex items-center justify-center text-5xl shadow-2xl border-4 border-inherit animate-bounce">
                            {feedback === 'correct' ? '⏰' : '⌛'}
                        </div>

                        <div className="text-center space-y-4 pt-8">
                            <h2 className={`text-2xl md:text-5xl font-black uppercase tracking-tighter italic font-serif ${feedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback === 'correct' ? 'LAIKAS TIKSLUS!' : 'NETINKAMAS LAIKAS!'}
                            </h2>

                            <div className={`p-4 md:p-8 rounded-2xl border-2 italic font-serif leading-relaxed ${feedback === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-sm md:text-xl font-bold text-slate-800 leading-relaxed">
                                    {feedback === 'correct'
                                        ? 'Nuostabu! Laikrodžio rodykles nustatei idealiai tiksliai.'
                                        : 'Oi! Rodyklės rodo ne tą laiką. Pabandyk dar kartą!'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default TimeClockGame;
